import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { SellerPageContainer } from '@/components/seller/layout/SellerPageContainer';
import { sellerService } from '@/services/seller';
import { useToast } from '@/components/common/Toast';
import { DiscountRateGuide } from '@/components/seller/discount/DiscountRateGuide';
import { TimeSettingSection } from '@/components/seller/discount/sections/TimeSettingSection';
import { DiscountSaveButton } from '@/components/seller/discount/DiscountSaveButton';
import { EcoPackTemplateSection, EcoPackTemplate } from '@/components/seller/discount/sections/EcoPackTemplateSection';
import { useDiscountTime } from '@/hooks/discount/useDiscountTime';
import { calculateDefaultPickupTimes } from '@/services/seller/discount/discount-defaults';
import { isValidTimeRange as isValidTimeRangeFn, generateTimeOptions } from '@/services/seller/discount/discount-time';
import { batchUpsertProductOffers, setOffersActiveForProducts } from '@/services/seller/discount/discount-offers';
import { upsertProductStock } from '@/services/seller/seller-helpers';
import { getKoreanDate } from '@/lib/dateUtils';
import { DEFAULT_ECOPACK_IMAGE_URL, LEGACY_ECOPACK_IMAGE_URL } from '@/constants';

export function DeadlineDiscountPage() {
  const navigate = useNavigate();
  const { ToastContainer, showToast } = useToast();
  const [restaurantId, setRestaurantId] = useState<string | null>(null);
  const [defaultPickupTimes, setDefaultPickupTimes] = useState({
    start: '18:00',
    end: '20:00'
  });
  const defaultDiscountRate = 40;
  const [saving, setSaving] = useState(false);
  const [showValidation, setShowValidation] = useState(false);


  const [templates, setTemplates] = useState<EcoPackTemplate[]>([]);
  const [removedTemplateIds, setRemovedTemplateIds] = useState<string[]>([]);
  const [enabled, setEnabled] = useState(true);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [useCustomOrderWindow, setUseCustomOrderWindow] = useState(false);
  const [customOrderStartTime, setCustomOrderStartTime] = useState('');
  const [customOrderEndTime, setCustomOrderEndTime] = useState('');

  // 시간 관리 훅 (수령/주문 시간)
  const {
    pickupStartTime,
    pickupEndTime,
    orderStartTime,
    orderEndTime,
    setPickupStartTime,
    setPickupEndTime,
    isValidTimeRange: isValidRange
  } = useDiscountTime({
    initialPickupStartTime: defaultPickupTimes.start,
    initialPickupEndTime: defaultPickupTimes.end,
    offerType: 'DEADLINE_DISCOUNT'
  });

  // 00:00~23:30 범위를 모두 노출/스냅
  const timeOptions = useMemo(() => generateTimeOptions(0, 23), []);
  const toMinutes = (time: string) => {
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
  };
  const createClientId = () => `client-${Math.random().toString(36).slice(2, 10)}`;
  const snapToOption = (time: string) => {
    if (!timeOptions.length) return time;
    if (timeOptions.includes(time)) return time;
    const target = toMinutes(time);
    let nearest = timeOptions[0];
    let diff = Math.abs(target - toMinutes(nearest));
    for (const opt of timeOptions) {
      const d = Math.abs(target - toMinutes(opt));
      if (d < diff) {
        diff = d;
        nearest = opt;
      }
    }
    return nearest;
  };

  const normalizeImageUrl = (imageUrl?: string | null) => {
    if (!imageUrl || imageUrl === LEGACY_ECOPACK_IMAGE_URL) return undefined;
    return imageUrl;
  };

  const applyTemplateDefaults = (template: EcoPackTemplate): EcoPackTemplate => ({
    ...template,
    category: template.category || '에코팩',
    imageUrl: template.imageUrl,
    templateType: template.templateType || (template.size ? 'preset' : 'custom'),
    clientId: template.clientId || createClientId(),
    selected: template.selected ?? true
  });

  const sortTemplates = (list: EcoPackTemplate[]) => {
    const selected = list.filter((t) => t.selected !== false);
    const unselected = list.filter((t) => t.selected === false);
    return [...selected, ...unselected];
  };

  const normalizeTime = (time?: string | null) => {
    if (!time) return undefined;
    if (/^\d{2}:\d{2}$/.test(time)) return time;
    if (/^\d{2}:\d{2}:\d{2}/.test(time)) return time.slice(0, 5);
    return time;
  };

  // 레스토랑 ID 가져오기 및 기본값 설정
  useEffect(() => {
    const getRestaurantIdAndDefaults = async () => {
      const result = await sellerService.getSellerRestaurantId();
      if (result.success && result.restaurantId) {
        setRestaurantId(result.restaurantId);
        
        // 영업시간 기반 기본값 계산
        const defaults = await calculateDefaultPickupTimes(result.restaurantId);
        setDefaultPickupTimes(defaults);
        setPickupStartTime(defaults.start);
        setPickupEndTime(defaults.end);

        // 템플릿/토글/주문시간 설정 로드
        setLoadingTemplates(true);
        const [templateResult, toggleResult, offersResult, orderWindowResult] = await Promise.all([
          sellerService.getDeadlineTemplateProducts(result.restaurantId),
          sellerService.getDeadlineDiscountToggle(result.restaurantId),
          sellerService.getProductOffers(result.restaurantId, 'DEADLINE_DISCOUNT', { includeInactive: true }),
          sellerService.getDeadlineOrderWindow(result.restaurantId)
        ]);

        if (toggleResult.success && typeof toggleResult.enabled === 'boolean') {
          setEnabled(toggleResult.enabled);
        }

        const offerMap = new Map<string, { rate: number; price: number; pickupStart?: string; pickupEnd?: string }>();
        if (offersResult.success && offersResult.data) {
          offersResult.data.forEach((o: any) => {
            offerMap.set(o.product_id, {
              rate: o.discount_rate ?? defaultDiscountRate,
              price: o.discounted_price ?? 0,
              pickupStart: o.deadline_pickup_start_time,
              pickupEnd: o.deadline_pickup_end_time
            });
          });
        }

        if (templateResult.success && templateResult.data) {
          const mapped: EcoPackTemplate[] = templateResult.data.map((item: any) => {
            const name: string = item.name || '';
            const size: EcoPackTemplate['size'] = name.includes('XL')
              ? 'XL'
              : name.includes('L')
                ? 'L'
                : name.includes('M')
                  ? 'M'
                  : name.includes('S')
                    ? 'S'
                    : undefined;
            const templateType: EcoPackTemplate['templateType'] = size ? 'preset' : 'custom';
            const offer = offerMap.get(item.id);
            const rate = offer?.rate ?? defaultDiscountRate;
          const discountedPrice = offer?.price ?? Math.floor((item.original_price || 0) * (100 - rate) / 100);
            return applyTemplateDefaults({
              id: item.id,
              name: item.name,
              description: item.description || '',
              originalPrice: item.original_price || 0,
              stock: item.stock_quantity || 0,
              imageUrl: normalizeImageUrl(item.image_url),
              category: item.category || '에코팩',
              size,
              templateType,
              discountRate: rate,
              discountedPrice,
              selected: item.is_available ?? true
            });
          });

          if (mapped.length > 0) {
            setTemplates(sortTemplates(mapped));
          } else {
            setTemplates(sortTemplates(([
              { name: '에코팩 S', size: 'S', originalPrice: 2000, stock: 0, description: '', category: '에코팩', discountRate: defaultDiscountRate, discountedPrice: 2000 * 0.6, templateType: 'preset' },
              { name: '에코팩 M', size: 'M', originalPrice: 5000, stock: 0, description: '', category: '에코팩', discountRate: defaultDiscountRate, discountedPrice: 5000 * 0.6, templateType: 'preset' },
            ] as EcoPackTemplate[]).map(applyTemplateDefaults)));
          }
        } else {
          // 템플릿 없을 때만 기본 S/M 제공
          setTemplates(sortTemplates(([
            { name: '에코팩 S', size: 'S', originalPrice: 2000, stock: 0, description: '', category: '에코팩', discountRate: defaultDiscountRate, discountedPrice: 2000 * 0.6, templateType: 'preset' },
            { name: '에코팩 M', size: 'M', originalPrice: 5000, stock: 0, description: '', category: '에코팩', discountRate: defaultDiscountRate, discountedPrice: 5000 * 0.6, templateType: 'preset' },
          ] as EcoPackTemplate[]).map(applyTemplateDefaults)));
        }

        // 기존에 저장된 마감할인 수령시간이 있으면 불러와서 적용
        if (offersResult.success && offersResult.data?.length) {
          const offerWithTimes = offersResult.data.find(
            (o) => o.deadline_pickup_start_time && o.deadline_pickup_end_time
          );

          const start = normalizeTime(offerWithTimes?.deadline_pickup_start_time);
          const end = normalizeTime(offerWithTimes?.deadline_pickup_end_time);

          const fallbackStart = defaultPickupTimes.start || '18:00';
          const fallbackEnd = defaultPickupTimes.end || '20:00';

          if (start && end && isValidTimeRangeFn(start, end)) {
            setPickupStartTime(snapToOption(start));
            setPickupEndTime(snapToOption(end));
          } else {
            console.warn('Invalid or missing pickup times, applying defaults', { start, end });
            setPickupStartTime(snapToOption(fallbackStart));
            setPickupEndTime(snapToOption(fallbackEnd));
          }
        }

        // 주문 가능 시간 커스텀 설정 적용
        if (orderWindowResult.success && orderWindowResult.data) {
          const { useCustom, customOrderStartTime, customOrderEndTime } = orderWindowResult.data;
          setUseCustomOrderWindow(useCustom);
          if (useCustom) {
            setCustomOrderStartTime(normalizeTime(customOrderStartTime) || orderStartTime);
            setCustomOrderEndTime(normalizeTime(customOrderEndTime) || orderEndTime);
          } else {
            setCustomOrderStartTime(orderStartTime);
            setCustomOrderEndTime(orderEndTime);
          }
        } else {
          setUseCustomOrderWindow(false);
          setCustomOrderStartTime(orderStartTime);
          setCustomOrderEndTime(orderEndTime);
        }

        setLoadingTemplates(false);
      } else {
        showToast(result.error || '레스토랑 정보를 찾을 수 없습니다');
      }
      setLoadingTemplates(false);
    };
    getRestaurantIdAndDefaults();
  }, [showToast]);

  const selectedTemplates = useMemo(
    () => templates.filter((t) => t.selected !== false),
    [templates]
  );
  const selectedCount = selectedTemplates.length;
  const hasInvalidSelected = selectedTemplates.some(t => !t.originalPrice || !t.stock);

  const isSaving = saving || loadingTemplates;

  const handleSave = async () => {
    if (!restaurantId) return;
    // 기본 검증: 가격/수량 필수, 시간 유효
    const invalid = selectedTemplates.some(t => !t.originalPrice || !t.stock);
    if (invalid) {
      setShowValidation(true);
      showToast('가격과 수량은 필수입니다');
      return;
    }
    if (!isValidRange) {
      showToast('수령 가능 시간을 확인해주세요');
      return;
    }
    if (useCustomOrderWindow) {
      if (!customOrderStartTime || !customOrderEndTime || !isValidTimeRangeFn(customOrderStartTime, customOrderEndTime)) {
        showToast('주문 가능 시간을 확인해주세요');
        return;
      }
    }

    try {
      setSaving(true);
      // upsert 템플릿
      const upsertResult = await sellerService.upsertDeadlineTemplateProducts(
        restaurantId,
        templates.map(t => ({
          id: t.id,
          name: t.name,
          description: t.description,
          price: t.originalPrice,
          stock: t.stock,
          category: t.category || '에코팩',
          isAvailable: t.selected !== false
        }))
      );
      if (!upsertResult.success || !upsertResult.productIds) throw new Error(upsertResult.error);

      // 삭제된 템플릿 제거
      for (const id of removedTemplateIds) {
        await sellerService.deleteDeadlineTemplateProduct(id);
      }

      // 오퍼/재고 저장 전 이미지/카테고리 동기화
      const templateWithIds = templates.map((t, idx) => ({
        ...t,
        id: upsertResult.productIds?.[idx] || t.id,
        category: t.category || '에코팩',
        imageUrl: t.imageUrl,
        selected: t.selected ?? true
      }));

      const resolvedProductIds = templateWithIds.map(t => t.id).filter((id): id is string => Boolean(id));
      if (resolvedProductIds.length !== templateWithIds.length) {
        throw new Error('상품 ID 매핑에 실패했습니다');
      }

      const syncResult = await sellerService.syncDeadlineTemplateImages({
        templates: templateWithIds,
        productIds: resolvedProductIds,
      });

      if (!syncResult.success) throw new Error(syncResult.error);

      const activeTemplates = templateWithIds.filter((t) => t.selected !== false);
      const inactiveTemplates = templateWithIds.filter((t) => t.selected === false);

      await batchUpsertProductOffers(
        activeTemplates.map(t => ({
          productId: t.id!,
          discountRate: t.discountRate ?? defaultDiscountRate,
          discountedPrice: t.discountedPrice ?? Math.floor(t.originalPrice * (100 - (t.discountRate ?? defaultDiscountRate)) / 100),
          stock: t.stock,
          pickupStartTime,
          pickupEndTime
        })),
        'DEADLINE_DISCOUNT',
        restaurantId,
        {
          useCustomOrderWindow,
          customOrderStartTime: useCustomOrderWindow ? customOrderStartTime : undefined,
          customOrderEndTime: useCustomOrderWindow ? customOrderEndTime : undefined
        }
      );

      const today = getKoreanDate();
      for (const t of activeTemplates) {
        if (t.id) {
          await upsertProductStock(t.id, today, t.stock);
        }
      }

      const activeIds = activeTemplates.map(t => t.id!).filter(Boolean);
      const inactiveIds = inactiveTemplates.map(t => t.id!).filter(Boolean);
      const allIds = [...activeIds, ...inactiveIds];
      if (!enabled) {
        await setOffersActiveForProducts(allIds, 'DEADLINE_DISCOUNT', false);
        await sellerService.cancelDeadlineOrdersByProducts(restaurantId, allIds, '마감할인 비활성화로 인한 취소');
      } else {
        // 토글 ON 시 비활성화된 오퍼를 활성화해 노출 복구
        await setOffersActiveForProducts(inactiveIds, 'DEADLINE_DISCOUNT', false);
        await setOffersActiveForProducts(activeIds, 'DEADLINE_DISCOUNT', true);
      }

      // 토글 반영
      await sellerService.updateDeadlineDiscountToggle(restaurantId, enabled);
      setShowValidation(false);

      showToast('마감할인 에코팩이 저장되었습니다');
      setTimeout(() => navigate('/seller/store'), 2000);
    } catch (e) {
      console.error(e);
      showToast('저장에 실패했습니다');
    } finally {
      setSaving(false);
    }
  };

  const effectiveOrderStartTime = useCustomOrderWindow
    ? (customOrderStartTime || orderStartTime)
    : orderStartTime;
  const effectiveOrderEndTime = useCustomOrderWindow
    ? (customOrderEndTime || orderEndTime)
    : orderEndTime;

  if (loadingTemplates) {
    return (
      <SellerPageContainer activeNavId="store">
        <div className="min-h-screen bg-gray-100 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
            <p className="text-gray-600">상품 목록을 불러오는 중...</p>
          </div>
        </div>
      </SellerPageContainer>
    );
  }

  return (
    <SellerPageContainer activeNavId="store">
      <div className="pb-32">
        {/* 헤더 */}
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate('/seller/store')}
            className="mr-4 p-2 rounded-lg hover:bg-gray-100"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-xl font-bold text-gray-800">오늘의 마감할인</h1>
        </div>

        {/* 할인율 안내 */}
        <DiscountRateGuide type="deadline" />

        {/* 마감할인 운영 토글 */}
        <div className="flex items-center justify-between mb-4 p-4 bg-white rounded-lg border border-gray-200">
          <div>
            <p className="text-sm text-gray-600">마감할인 운영</p>
            <p className="text-xs text-gray-500">꺼두면 오늘 마감할인이 비활성화되고 주문이 취소됩니다</p>
          </div>
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:bg-green-500 relative">
              <div className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition peer-checked:translate-x-5"></div>
            </div>
          </label>
        </div>

        {/* 에코팩 템플릿 섹션 */}
        <EcoPackTemplateSection
          templates={templates}
          onChange={(index, template) => {
            setTemplates(prev => sortTemplates(prev.map((item, i) => i === index ? template : item)));
          }}
          onAdd={(size) => {
            setTemplates(prev => sortTemplates([...prev, applyTemplateDefaults({
              name: `에코팩 ${size}`,
              size,
              originalPrice: 0,
              stock: 0,
              description: '',
              category: '에코팩',
              discountRate: defaultDiscountRate,
              discountedPrice: 0,
              templateType: 'preset',
              selected: true
            } as EcoPackTemplate)]));
          }}
          onAddCustom={() => {
            setTemplates(prev => sortTemplates([...prev, applyTemplateDefaults({
              name: '',
              originalPrice: 0,
              stock: 0,
              description: '',
              category: '에코팩',
              discountRate: defaultDiscountRate,
              discountedPrice: 0,
              templateType: 'custom',
              selected: true
            } as EcoPackTemplate)]));
          }}
          onRemove={(index) => {
            setTemplates(prev => {
              const target = prev[index];
              if (target.id) setRemovedTemplateIds(ids => [...ids, target.id!]);
              return sortTemplates(prev.filter((_, i) => i !== index));
            });
          }}
          showValidation={showValidation}
          fallbackImageUrl={DEFAULT_ECOPACK_IMAGE_URL}
        />

        {/* 주문시간 커스텀 토글 */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-700 font-medium">주문가능시간 커스텀</p>
              <p className="text-xs text-gray-500">수령시간과 별도로 주문 가능 시간을 설정합니다</p>
            </div>
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={useCustomOrderWindow}
                onChange={(e) => {
                  const next = e.target.checked;
                  setUseCustomOrderWindow(next);
                  if (next) {
                    if (!customOrderStartTime) setCustomOrderStartTime(orderStartTime);
                    if (!customOrderEndTime) setCustomOrderEndTime(orderEndTime);
                  }
                }}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:bg-green-500 relative">
                <div className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition peer-checked:translate-x-5"></div>
              </div>
            </label>
          </div>

          {useCustomOrderWindow && (
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">주문 시작</label>
                <select
                  value={customOrderStartTime}
                  onChange={(e) => setCustomOrderStartTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  {timeOptions.map(time => (
                    <option key={time} value={time}>{time}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">주문 종료</label>
                <select
                  value={customOrderEndTime}
                  onChange={(e) => setCustomOrderEndTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  {timeOptions.map(time => (
                    <option key={time} value={time}>{time}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* 수령가능시간 설정 */}
        <TimeSettingSection
          type="deadline"
          pickupStartTime={pickupStartTime}
          pickupEndTime={pickupEndTime}
          onPickupStartTimeChange={(time) => setPickupStartTime(time)}
          onPickupEndTimeChange={(time) => setPickupEndTime(time)}
          orderStartTime={effectiveOrderStartTime}
          orderEndTime={effectiveOrderEndTime}
          showOrderHelper={!useCustomOrderWindow}
        />

        {/* 저장 버튼 */}
        <DiscountSaveButton
          type="deadline"
          canSave={templates.length > 0 && !hasInvalidSelected && isValidRange && !isSaving}
          saving={isSaving}
          selectedCount={selectedCount}
          onSave={handleSave}
          isFloating={true}
        />
      </div>

      <ToastContainer />
    </SellerPageContainer>
  );
}
